import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  ChevronLeft,
  ChevronDown,
  Search,
  Bell,
  Sun,
  Moon,
  Check,
  ArrowDownToLine,
  ArrowUpFromLine,
  CircleHelp,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCompany } from '@/contexts/CompanyContext';
import { useDirection, type ShippingDirection } from '@/contexts/DirectionContext';
import { useSearchPalette } from '@/features/search/hooks/useSearchPalette';

const PAGE_TITLE_KEY: Record<string, string> = {
  dashboard: 'nav.dashboard',
  containers: 'nav.containers',
  bills: 'nav.bills',
  map: 'nav.map',
  offers: 'nav.offers',
  costs: 'nav.costs',
  'ai-chat': 'nav.assistant',
  'two-factor': 'nav.two_factor',
  settings: 'nav.settings',
  profile: 'nav.profile',
  help: 'nav.help',
  contact: 'nav.contact',
};

function usePageTitleKey() {
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);
  // /app/<page>/...
  const page = segments[1];
  return PAGE_TITLE_KEY[page] || 'nav.dashboard';
}

interface SiteHeaderProps {
  onToggleSidebar: () => void;
  onOpenMobileSidebar: () => void;
}

export function SiteHeader({ onToggleSidebar, onOpenMobileSidebar }: SiteHeaderProps) {
  const { t } = useLanguage();
  const titleKey = usePageTitleKey();
  const { open: openSearch } = useSearchPalette();

  return (
    <header className="header">
      <div className="header-left">
        <button
          type="button"
          className="mobile-header-toggle"
          onClick={onOpenMobileSidebar}
          aria-label={t('common.open_menu')}
        >
          <Menu size={20} />
        </button>
        <button
          type="button"
          className="header-toggle-sidebar hidden-mobile"
          onClick={onToggleSidebar}
          aria-label={t('common.toggle_sidebar')}
          title={t('common.toggle_sidebar')}
        >
          <ChevronLeft size={16} strokeWidth={2} />
        </button>
        <div className="page-title">{t(titleKey)}</div>
      </div>

      <button
        type="button"
        className="header-search hidden-mobile"
        aria-label="Open command palette"
        data-tour="search"
        onClick={openSearch}
      >
        <Search size={16} />
        <span className="placeholder">{t('header.search.placeholder')}</span>
        <kbd>⌘K</kbd>
      </button>

      <div className="header-right">
        <DirectionSwitcher />
        <CompanySwitcher />
        <NotificationsButton />
        {/* limba + tema, țintă comună pentru turul ghidat (pe telefon butoanele sunt ascunse) */}
        <div className="hidden-mobile" data-tour="preferences" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LanguageToggle />
          <ThemeToggle />
        </div>
        <HelpButton />
        <button
          type="button"
          className="icon-btn show-mobile"
          aria-label="Search"
          onClick={openSearch}
          data-tour="search"
        >
          <Search size={18} />
        </button>
      </div>
    </header>
  );
}

/* ---------------- Direction switcher ---------------- */

const DIRECTION_META: Record<
  ShippingDirection,
  { labelKey: string; color: string; Icon: typeof ArrowDownToLine }
> = {
  import: { labelKey: 'direction.import', color: 'var(--accent)', Icon: ArrowDownToLine },
  export: { labelKey: 'direction.export', color: '#0f766e', Icon: ArrowUpFromLine },
};

function DirectionSwitcher() {
  const { t } = useLanguage();
  const { direction, setDirection, availableDirections, hasBothDirections } = useDirection();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Conturile cu o singură direcție văd exact UI-ul de dinainte, fără selector.
  if (!hasBothDirections) return null;

  const current = DIRECTION_META[direction];
  const CurrentIcon = current.Icon;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="company-switcher direction-switcher"
        data-tour="direction"
        onClick={() => setOpen((v) => !v)}
        aria-label={`${t('direction.label')}: ${t(current.labelKey)}`}
        title={t(current.labelKey)}
      >
        <div className="swatch" style={{ background: current.color }}>
          <CurrentIcon size={14} />
        </div>
        <div className="info">
          <span className="label">{t('direction.label')}</span>
          <span className="name">{t(current.labelKey)}</span>
        </div>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="dropdown dropdown-company">
          {availableDirections.map((d) => {
            const meta = DIRECTION_META[d];
            const Icon = meta.Icon;
            const selected = d === direction;
            return (
              <button
                key={d}
                type="button"
                className={'dropdown-item' + (selected ? ' selected' : '')}
                onClick={() => {
                  setDirection(d);
                  setOpen(false);
                }}
              >
                <div
                  className="swatch"
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 6,
                    background: meta.color,
                    display: 'grid',
                    placeItems: 'center',
                    color: 'white',
                    fontSize: 10,
                    fontWeight: 600,
                  }}
                >
                  <Icon size={14} />
                </div>
                <span style={{ flex: 1 }}>{t(meta.labelKey)}</span>
                {selected && <Check size={14} className="check" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------- Company switcher ---------------- */

function CompanySwitcher() {
  const { t } = useLanguage();
  const {
    companies,
    selectedCompanyId,
    setSelectedCompanyId,
    selectedCompany,
    isAllSelected,
    isLoading,
  } = useCompany();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const display = isAllSelected || !selectedCompany
    ? { name: t('common.all_companies'), short: 'ALL', color: 'var(--text-muted)' }
    : selectedCompany;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" className="company-switcher" data-tour="company" onClick={() => setOpen((v) => !v)}>
        <div className="swatch" style={{ background: display.color }}>
          {display.short}
        </div>
        <div className="info">
          <span className="label">{t('common.company')}</span>
          <span className="name">{display.name}</span>
        </div>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="dropdown dropdown-company">
          <button
            type="button"
            className={'dropdown-item' + (isAllSelected ? ' selected' : '')}
            onClick={() => {
              setSelectedCompanyId('all');
              setOpen(false);
            }}
          >
            <div
              className="swatch"
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                background: 'var(--text-muted)',
                display: 'grid',
                placeItems: 'center',
                color: 'white',
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              ALL
            </div>
            <span style={{ flex: 1 }}>{t('common.all_companies')}</span>
            {isAllSelected && <Check size={14} className="check" />}
          </button>
          <div className="dropdown-divider" />
          {isLoading && companies.length === 0 ? (
            <div className="muted fs-12" style={{ padding: '12px', textAlign: 'center' }}>
              …
            </div>
          ) : (
            companies.map((co) => {
              const selected = selectedCompanyId === co.id;
              return (
                <button
                  key={co.id}
                  type="button"
                  className={'dropdown-item' + (selected ? ' selected' : '')}
                  onClick={() => {
                    setSelectedCompanyId(co.id);
                    setOpen(false);
                  }}
                >
                  <div
                    className="swatch"
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 6,
                      background: co.color,
                      display: 'grid',
                      placeItems: 'center',
                      color: 'white',
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  >
                    {co.short}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{co.name}</div>
                    {co.vat && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{co.vat}</div>
                    )}
                  </div>
                  {selected && <Check size={14} className="check" />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- Notifications ---------------- */

interface Notification {
  id: number;
  title: string;
  body: string;
  unread?: boolean;
}

// TODO: înlocuiește cu date reale din API
const MOCK_NOTIFICATIONS: Notification[] = [];

function NotificationsButton() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const items = MOCK_NOTIFICATIONS;
  const unread = items.filter((n) => n.unread).length;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="icon-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('common.notifications')}
      >
        <Bell size={18} />
        {unread > 0 && <span className="dot" />}
      </button>
      {open && (
        <div className="dropdown dropdown-notif">
          <div
            style={{
              padding: '8px 10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600 }}>{t('common.notifications')}</div>
            <span className="muted fs-11">
              {unread} {t('common.unread')}
            </span>
          </div>
          <div className="dropdown-divider" />
          {items.length === 0 ? (
            <div className="muted fs-12" style={{ padding: '16px 12px', textAlign: 'center' }}>
              {t('common.no_results')}
            </div>
          ) : (
            items.map((n) => (
              <div
                key={n.id}
                className="dropdown-item"
                style={{ alignItems: 'flex-start', padding: '10px', gap: 10, cursor: 'default' }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.35 }}>{n.title}</div>
                  <div className="muted fs-12" style={{ marginTop: 2 }}>
                    {n.body}
                  </div>
                </div>
                {n.unread && (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 99,
                      background: 'var(--accent)',
                      flexShrink: 0,
                      marginTop: 6,
                    }}
                  />
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- Help ---------------- */

function HelpButton() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="icon-btn"
      onClick={() => navigate('/app/help')}
      aria-label={t('nav.help')}
      title={t('nav.help')}
      data-tour="help"
    >
      <CircleHelp size={18} />
    </button>
  );
}

/* ---------------- Language toggle ---------------- */

function LanguageToggle() {
  const { currentLanguage, changeLanguage, t } = useLanguage();
  return (
    <button
      type="button"
      className="icon-btn hidden-mobile"
      onClick={() => changeLanguage(currentLanguage === 'ro' ? 'en' : 'ro')}
      aria-label={t('common.change_language')}
      title={t('common.change_language')}
    >
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' }}>
        {currentLanguage.toUpperCase()}
      </span>
    </button>
  );
}

/* ---------------- Theme toggle ---------------- */

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const { t } = useLanguage();
  // Determină tema efectivă pentru a afișa iconul corect
  const isDark =
    theme === 'dark' ||
    (theme === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);

  return (
    <button
      type="button"
      className="icon-btn hidden-mobile"
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label={t('common.toggle_theme')}
      title={t('common.toggle_theme')}
    >
      {isDark ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  );
}
